import { useState } from 'react';
import { FiSun, FiMoon, FiSave, FiPlus, FiList, FiDownload, FiSettings, FiMessageSquare, FiKey } from 'react-icons/fi';
import { useSettings } from '../../contexts/SettingsContext';
import { useDiagram } from '../../contexts/DiagramContext';
import './Header.css';

const Header = ({ 
  aiEnabled, 
  showAiChat, 
  setShowAiChat,
  setSelectedCode,
  openApiKeyModal,
  hasApiKey,
  togglePersonaModal
}) => {
  const { settings, toggleTheme } = useSettings();
  const { saveDiagram, newDiagram, currentDiagramId, code } = useDiagram();
  const [showSavedDiagrams, setShowSavedDiagrams] = useState(false);
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [diagramTitle, setDiagramTitle] = useState('');

  // Handle save button click
  const handleSave = () => {
    if (currentDiagramId) {
      // If we're editing an existing diagram, just save it
      saveDiagram();
    } else {
      // If it's a new diagram, open the save dialog
      setSaveDialogOpen(true);
    }
  };

  // Handle save dialog confirm
  const handleSaveConfirm = () => {
    saveDiagram(diagramTitle);
    setSaveDialogOpen(false);
    setDiagramTitle('');
  };

  // Toggle saved diagrams panel
  const toggleSavedDiagrams = () => {
    setShowSavedDiagrams(!showSavedDiagrams);
    
    // Update the class of the saved diagrams container
    const container = document.querySelector('.saved-diagrams-container');
    if (container) {
      container.classList.toggle('visible', !showSavedDiagrams);
    }
  };

  // Toggle AI chat
  const toggleAiChat = () => {
    // Set the selected code for the chat when opening
    if (!showAiChat && setSelectedCode) {
      setSelectedCode();
    }
    
    // Toggle the chat visibility
    console.log("Chat button clicked, current state:", showAiChat);
    setShowAiChat(!showAiChat);
    
    // Log the action for debugging
    console.log("Toggling AI Chat to:", !showAiChat);
  };

  return (
    <header className="header">
      <div className="logo">
        <svg 
          width="24" 
          height="24" 
          viewBox="0 0 24 24" 
          fill="none" 
          xmlns="http://www.w3.org/2000/svg"
          className="logo-svg"
        >
          <path 
            d="M12 2L2 7L12 12L22 7L12 2Z" 
            stroke="currentColor" 
            strokeWidth="2" 
            strokeLinecap="round" 
            strokeLinejoin="round"
          />
          <path 
            d="M2 17L12 22L22 17" 
            stroke="currentColor" 
            strokeWidth="2" 
            strokeLinecap="round" 
            strokeLinejoin="round"
          />
          <path 
            d="M2 12L12 17L22 12" 
            stroke="currentColor" 
            strokeWidth="2" 
            strokeLinecap="round" 
            strokeLinejoin="round"
          />
        </svg>
        <span>Diagram Studio</span>
      </div>
      
      <div className="header-actions">
        {/* API Status Indicator */}
        <div className={`api-status ${hasApiKey ? 'active' : 'inactive'}`}>
          <FiKey size={16} />
          <span className="hidden md:inline ml-1">
            {hasApiKey ? 'API Active' : 'API Key Required'}
          </span>
        </div>
        
        <button 
          className="btn btn-secondary" 
          onClick={newDiagram}
          title="New Diagram"
        >
          <FiPlus size={18} />
          <span className="hidden md:inline ml-1">New</span>
        </button>
        
        <button 
          className="btn btn-primary" 
          onClick={handleSave}
          title="Save Diagram"
        >
          <FiSave size={18} />
          <span className="hidden md:inline ml-1">Save</span>
        </button>
        
        <button 
          className={`btn btn-secondary ${showSavedDiagrams ? 'active' : ''}`} 
          onClick={toggleSavedDiagrams}
          title="Saved Diagrams"
        >
          <FiList size={18} />
          <span className="hidden md:inline ml-1">Saved</span>
        </button>
        
        <button 
          className="btn btn-icon btn-secondary" 
          onClick={toggleTheme}
          title={settings.theme === 'dark' ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
        >
          {settings.theme === 'dark' ? <FiSun size={18} /> : <FiMoon size={18} />}
        </button>

        {/* API Key Configuration Button */}
        <button 
          className={`btn btn-icon ${hasApiKey ? 'btn-secondary' : 'btn-warning pulse-animation'}`}
          onClick={openApiKeyModal}
          title="Configure API Key"
        >
          <FiKey size={18} />
        </button>

        <button 
          className="btn btn-icon btn-secondary" 
          onClick={togglePersonaModal}
          title="Customize Persona"
        >
          <FiSettings size={18} />
        </button>
      </div>
      
      {/* Save Dialog */}
      {saveDialogOpen && (
        <div className="save-dialog-overlay">
          <div className="save-dialog">
            <h3>Save Diagram</h3>
            <input
              type="text"
              placeholder="Diagram Title"
              value={diagramTitle}
              onChange={(e) => setDiagramTitle(e.target.value)}
              autoFocus
            />
            <div className="save-dialog-actions">
              <button 
                className="btn btn-secondary" 
                onClick={() => setSaveDialogOpen(false)}
              >
                Cancel
              </button>
              <button 
                className="btn btn-primary" 
                onClick={handleSaveConfirm}
                disabled={!diagramTitle.trim()}
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header; 